// Dependencies
import React, { useState } from 'react';

import _ from 'lodash';
import { connect } from 'react-redux';

// Utils
import analytics from '@nerdwallet/analytics';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { Tooltip, Stack, Type, Box } from '@nerdwallet/currency';
import { Tracking } from '@nerdwallet/nw-client-lib';
import { ControlledAccordion } from '@nerdwallet/react-accordion';

import {
  ENTITY_NAME,
  EVENT_TYPE,
  FORM_NAME,
  MORTGAGE_TYPE,
  SECTION_NAME,
  FIELD_NAME,
} from '../../../lib/analytics-constants';
import {
  manualInputsSelector,
  inputsSelector,
} from '../../../lib/selectors/refinance-calculator-selectors';
import {
  ReducerInputsData,
  RefinanceCalculatorField,
} from '../../../lib/utils';
import ControlPanel from '../../config-builder-free/control-panel/control-panel';
import {
  MortgageAmountInput,
  MortgageLoanTermInput,
  MortgageInterestRateInput,
  MortgageOriginationYearInput,
  RefinanceInterestRateInput,
  RefinanceGoalInput,
  RefinanceClosingCosts,
  RefinanceCashOutAmount,
  RefinanceLoanTermInput,
  PropertyLocation,
} from '../inputs';

import styles from './control-panel-view.module.less';

// Constants
const { formOpen } = Tracking;

interface RefinanceControlPanelProps
  extends Pick<
    ReducerInputsData<RefinanceCalculatorField>,
    'manualInputs' | 'inputs'
  > {
  enableTracking: boolean;
}

const CONTROL_PANEL = 'controlPanel';

/**
 * Select dropdown for the refinance goal input
 * @param
 * @returns ReactNode
 */
const getRefinanceGoalInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_REFINANCE_PURPOSE_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR_DETAILS,
      },
    },
  };
  return (
    <Box className={styles.topPanel}>
      <RefinanceGoalInput
        analytics={analyticsPayload}
        label="Reason for refinancing"
      />
    </Box>
  );
};

/**
 * Location dropdown React.Node
 */
const getLocationInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.US_LOCATION_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR_DETAILS,
        additional_attributes: {
          location_type: 'property_location',
        },
      },
    },
  };

  return (
    <Box className={styles.topPanel}>
      <PropertyLocation label="Location of home" analytics={analyticsPayload} />
    </Box>
  );
};

/**
 * Mortgage Amount input field
 * @returns InputMoney Node
 */
const getMortgageAmountInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_ORIGINAL_AMOUNT_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR_ORIGINAL_MORTGAGE,
      },
    },
  };
  return (
    <MortgageAmountInput
      label="Amount"
      tooltip="The amount you borrowed."
      analytics={analyticsPayload}
    />
  );
};

/**
 * Mortgage Loan Term input field
 */
const oldMortgageLoanTerm = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_TERM_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR_ORIGINAL_MORTGAGE,
        additional_attributes: {
          mortgage_type: MORTGAGE_TYPE.ORIGINAL,
        },
      },
    },
  };
  return (
    <Box>
      <Type size="1">
        Term
        <Tooltip
          placement="top"
          content="The number of years you have to repay your loan (often 30 or 15 years)."
        />
      </Type>
      <Box className={styles.numStepperWrapper}>
        <MortgageLoanTermInput
          analytics={analyticsPayload}
          label="Loan term"
          tooltip="The length of time you originally had to pay back your loan (usually 30 or 15 years)."
          inputType="RANGE"
        />
      </Box>
    </Box>
  );
};

/**
 * Mortgage Interest Rate input field
 * @returns InputPercent Node
 */
const getMortgageInterestRate = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_INTEREST_RATE_APR_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR_ORIGINAL_MORTGAGE,
        additional_attributes: {
          mortgage_type: MORTGAGE_TYPE.ORIGINAL,
        },
      },
    },
  };
  return (
    <MortgageInterestRateInput
      label="Interest rate"
      tooltip="You can find the interest rate (and the amount and term) on your loan paperwork."
      analytics={analyticsPayload}
    />
  );
};

/**
 * Mortgage Originating year input field
 * @returns Input node
 */
const getMortgageOriginatingYearInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_ORIGINATION_YEAR_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR_ORIGINAL_MORTGAGE,
      },
    },
  };

  return (
    <MortgageOriginationYearInput
      label="Origination year"
      tooltip="The year you took out your loan."
      analytics={analyticsPayload}
    />
  );
};

/**
 * Refinance Loan term input field
 */
const getRefinanceLoanTerm = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_TERM_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR_NEW_MORTGAGE,
        additional_attributes: {
          mortgage_type: MORTGAGE_TYPE.NEW,
        },
      },
    },
  };

  return (
    <Box>
      <Type size="1">
        Term
        <Tooltip
          placement="top"
          content="The length of the new loan, in years."
        />
      </Type>
      <Box className={styles.numStepperWrapper}>
        <RefinanceLoanTermInput
          label="Term"
          tooltip="The term, in years, of the refinanced loan you’re considering (which would replace your current one)."
          analytics={analyticsPayload}
          inputType="RANGE"
        />
      </Box>
    </Box>
  );
};

/**
 * Refinance Interest rate input field
 * @returns InputPercent Node
 */
const getRefinanceInterestRateInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_INTEREST_RATE_APR_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.REFI_CALCULATOR_NEW_MORTGAGE,
        additional_attributes: {
          mortgage_type: MORTGAGE_TYPE.NEW,
        },
      },
    },
  };
  return (
    <RefinanceInterestRateInput
      label="Interest rate"
      tooltip="The new interest rate."
      analytics={analyticsPayload}
    />
  );
};

/**
 * Refinance CashOut Amount input field
 * @returns InputMoney Node
 */
const getRefinanceCashOutAmount = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_CASH_OUT_AMOUNT_UPDATED,
        form_name: FORM_NAME.REFI_CALCULATOR_NEW_MORTGAGE,
      },
    },
  };
  return (
    <RefinanceCashOutAmount
      label="Cash-out amount"
      tooltip="The amount of your home's equity you plan to receive in cash. Your new loan will increase by this amount."
      analytics={analyticsPayload}
    />
  );
};

/**
 * Refinance Closing Costs input field
 * @returns InputMoney Node
 */
const getRefinanceClosingCosts = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
        fieldName: FIELD_NAME.REFI_CALC.CONTROL_PANEL.CLOSINGCOST,
      },
      bedrock: {
        eventType: EVENT_TYPE.HOME_CLOSING_COST_UPDATED,
        form_name: FORM_NAME.REFI_CALCULATOR_NEW_MORTGAGE,
      },
    },
  };
  return (
    <RefinanceClosingCosts
      label="Closing costs"
      tooltip="The origination, appraisal and other fees charged by the lender and third parties. Closing costs typically total 2% to 5% of the loan amount. We have estimated your closing costs as 3% of your refinance amount, but you may enter a different amount here."
      analytics={analyticsPayload}
    />
  );
};

/**
 * Refinance Calculator - Control Panel view
 */
const RefiControlPanelView: React.FC<RefinanceControlPanelProps> = ({
  enableTracking,
}) => {
  // state to manage accordion's activeKey
  // defaulted to have the first accordion child open
  const [expandedMap, setExpandedMap] = useState([true, false]);

  // accordion's activeKey
  const [activeAccordionChild, setActiveAccordionChild] = useState(0);

  const accordionPropsMap = [
    {
      entityName: ENTITY_NAME.MRT_REFI_CALCULATOR_ORIGINAL_MORTGAGE,
      formName: ENTITY_NAME.MRT_REFI_CALCULATOR_ORIGINAL_MORTGAGE,
      header: 'Original mortgage details',
    },
    {
      entityName: ENTITY_NAME.MRT_REFI_CALCULATOR_NEW_MORTGAGE,
      formName: ENTITY_NAME.MRT_REFI_CALCULATOR_NEW_MORTGAGE,
      header: 'New mortgage details',
    },
  ];

  /**
   * Toggles accordion
   * @param {number} index accordion key/id to open
   */
  const toggleItem = (index: number) => {
    const newExpandedMap = [false, false];
    newExpandedMap[index] = !expandedMap[index];

    const { entityName, formName, header } = accordionPropsMap[index];

    if (enableTracking) {
      formOpen({
        formName,
        fieldName: 'header',
        fieldValue: _.snakeCase(header),
      });
    }

    const uuid = analytics.uuid();
    analytics.track(EVENT_TYPE.ELEMENT_INTERACTED, {
      impression_id: uuid,
      element_name: entityName,
      action: 'open',
      interaction_type: 'press',
    });
    setExpandedMap(newExpandedMap);
    setActiveAccordionChild(index);
  };

  // Builds original mortgage accordion content
  const originalMortgageDetails = {
    title: <Type size="1">{accordionPropsMap[0].header}</Type>,
    children: (
      <Impression
        enabled={activeAccordionChild === 0}
        eventType={EVENT_TYPE.FORM_IMPRESSION}
        entityName={accordionPropsMap[0].entityName}
        payload={{
          form_name: accordionPropsMap[0].entityName,
          section_name: accordionPropsMap[0].entityName,
        }}
      >
        <Stack spacing={3}>
          {getMortgageAmountInput()}
          {oldMortgageLoanTerm()}
          {getMortgageInterestRate()}
          {getMortgageOriginatingYearInput()}
        </Stack>
      </Impression>
    ),
    initiallyExpanded: true,
    key: 'old_mortgage_details',
    expanded: expandedMap[0],
    onSelect: () => toggleItem(0),
  };
  // Builds new mortgage accordion content
  const newMortgageDetails = {
    title: <Type size="1"> {accordionPropsMap[1].header} </Type>,
    children: (
      <Impression
        enabled={activeAccordionChild === 1}
        eventType={EVENT_TYPE.FORM_IMPRESSION}
        entityName={accordionPropsMap[1].entityName}
        payload={{
          form_name: accordionPropsMap[1].entityName,
          section_name: accordionPropsMap[1].entityName,
        }}
      >
        <Stack spacing={3}>
          {getRefinanceLoanTerm()}
          {getRefinanceInterestRateInput()}
          {getRefinanceCashOutAmount()}
          {getRefinanceClosingCosts()}
        </Stack>
      </Impression>
    ),
    key: 'new_mortgage_details',
    expanded: expandedMap[1],
    onSelect: () => toggleItem(1),
  };

  // Builds entire panel content
  const panelContent = (
    <Stack spacing={3} className={styles.inputFieldWidth}>
      <Impression
        eventType={EVENT_TYPE.FORM_IMPRESSION}
        entityName={ENTITY_NAME.MRT_REFI_CALCULATOR_DETAILS}
        payload={{
          section_name: SECTION_NAME.MRT_REFI_CALCULATOR_DETAILS,
          form_name: FORM_NAME.REFI_CALCULATOR_DETAILS,
        }}
      >
        {getRefinanceGoalInput()}
        {getLocationInput()}
      </Impression>
      <ControlledAccordion
        id="mortgage_accordion"
        analytics={{
          enabled: true,
          payload: {
            elementName: ENTITY_NAME.MRT_REFI_CALCULATOR_ORIGINAL_MORTGAGE,
          },
        }}
        items={[originalMortgageDetails, newMortgageDetails]}
      />
    </Stack>
  );
  return <ControlPanel panelContent={panelContent} />;
};

const mapStateToProps = (state) => ({
  manualInputs: manualInputsSelector(state),
  inputs: inputsSelector(state),
});

export default connect(mapStateToProps)(RefiControlPanelView);
